import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { CostCenterOption } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/hooks/useCostCenterByLegalEntities'
import {
  DEFAULT_ASSIGNMENT_PERCENTAGE,
  defaultStructuredAssignment,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/defaults'
import {
  FormattedAssignment,
  FormattedAssignmentWithDeletion,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

interface UseStructuredAssignmentsProps {
  assignments: FormattedAssignment[]
  assignmentsToCreate: FormattedAssignment[]
  assignmentsToUpdate: FormattedAssignmentWithDeletion[]
  assignmentToCreateTemplate: FormattedAssignment[]
  selectedDateRange: { startDate: string; endDate?: string }
  setAssignments: React.Dispatch<React.SetStateAction<FormattedAssignment[]>>
  setAssignmentsToCreate: React.Dispatch<React.SetStateAction<FormattedAssignment[]>>
  setAssignmentsToUpdate: React.Dispatch<React.SetStateAction<FormattedAssignmentWithDeletion[]>>
  setAssignmentToCreateTemplate: React.Dispatch<React.SetStateAction<FormattedAssignment[]>>
}

export const useStructuredAssignments = ({
  assignments,
  assignmentsToCreate,
  assignmentsToUpdate,
  assignmentToCreateTemplate,
  selectedDateRange,
  setAssignments,
  setAssignmentsToCreate,
  setAssignmentsToUpdate,
  setAssignmentToCreateTemplate,
}: UseStructuredAssignmentsProps) => {
  //------------ Add New Structured Assignment ------------

  const addNewStructuredAssignment = useUnsafeCallback(() => {
    const newAssignment = defaultStructuredAssignment({
      startDate: String(selectedDateRange.startDate),
      endDate: selectedDateRange.endDate,
    })

    setAssignmentToCreateTemplate((prevAssignments) => [...prevAssignments, newAssignment])
    setAssignments([...assignments, newAssignment])

    track(trackingEvents.accounting.addNewCostCenterToAssignment, {
      countAdded:
        assignments.filter((item) => item.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE).length + 1,
    })
  }, [assignments, selectedDateRange, setAssignmentToCreateTemplate, setAssignments])

  //------------ Delete Structured Assignment ------------

  const deleteStructuredAssignment = (assignmentId: string) => {
    const storedAssignment = assignmentsToUpdate.find((v) => v.id === assignmentId)
    const assignmentInCreate = assignmentsToCreate.find((v) => v.id === assignmentId)
    const assignmentInTemplate = assignmentToCreateTemplate.find((v) => v.id === assignmentId)

    if (storedAssignment) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== storedAssignment.id),
        { ...storedAssignment, markedForDeletion: true },
      ])
    }

    if (assignmentInCreate) {
      setAssignmentsToCreate([...assignmentsToCreate.filter((v) => v.id !== assignmentId)])
    }

    if (assignmentInTemplate) {
      setAssignmentToCreateTemplate([
        ...assignmentToCreateTemplate.filter((v) => v.id !== assignmentId),
      ])
    }

    setAssignments([...assignments.filter((v) => v.id !== assignmentId)])
  }

  //------------ Update Structured Field (Generic) ------------

  const updateStructuredField = (assignmentId: string, updates: Partial<FormattedAssignment>) => {
    const assignment = assignments.find((v) => v.id === assignmentId)
    if (!assignment) return

    const updatedAssignment = { ...assignment, ...updates }

    // Check if this is in the update list (existing assignment)
    const existingInUpdate = assignmentsToUpdate.find((v) => v.id === assignmentId)
    if (existingInUpdate) {
      setAssignmentsToUpdate(
        assignmentsToUpdate.map((v) => (v.id === assignmentId ? { ...v, ...updates } : v))
      )
    }

    // Check if this is in the template (new assignment not yet valid)
    const existingInTemplate = assignmentToCreateTemplate.find((v) => v.id === assignmentId)
    if (existingInTemplate) {
      const updatedTemplateAssignment = { ...existingInTemplate, ...updates }

      // Check if assignment is now valid (has at least one cost center and valid percentage)
      const hasValidCostCenter =
        updatedTemplateAssignment.jobCatalogCostCenterId != null ||
        updatedTemplateAssignment.workplaceCostCenterId != null
      const hasValidPercentage =
        updatedTemplateAssignment.percentage !== DEFAULT_ASSIGNMENT_PERCENTAGE &&
        updatedTemplateAssignment.percentage > 0

      if (hasValidCostCenter && hasValidPercentage) {
        // Move from template to create list
        setAssignmentToCreateTemplate(
          assignmentToCreateTemplate.filter((v) => v.id !== assignmentId)
        )
        setAssignmentsToCreate([...assignmentsToCreate, updatedTemplateAssignment])
      } else {
        // Keep in template
        setAssignmentToCreateTemplate(
          assignmentToCreateTemplate.map((v) =>
            v.id === assignmentId ? updatedTemplateAssignment : v
          )
        )
      }
    }

    // Check if this is in the create list
    const existingInCreate = assignmentsToCreate.find((v) => v.id === assignmentId)
    if (existingInCreate) {
      setAssignmentsToCreate(
        assignmentsToCreate.map((v) => (v.id === assignmentId ? { ...v, ...updates } : v))
      )
    }

    // Update the display list
    setAssignments(assignments.map((item) => (item.id === assignmentId ? updatedAssignment : item)))
  }

  //------------ Update Job Structure ------------

  const updateJobStructure = (assignmentId: string, option: CostCenterOption | null) => {
    // If selecting a new cost center, remove it from any other assignments
    if (option) {
      const otherAssignments = assignments.filter((a) => a.id !== assignmentId)
      otherAssignments.forEach((otherAssignment) => {
        if (otherAssignment.jobCatalogCostCenterId === Number(option.id)) {
          // Remove the job structure from the other assignment
          updateStructuredField(otherAssignment.id, {
            jobCatalogCostCenterId: undefined,
            jobCatalogCostCenterName: undefined,
            jobCatalogNodeUuid: undefined,
          })
        }
      })
    }

    updateStructuredField(assignmentId, {
      jobCatalogCostCenterId: option ? Number(option.id) : undefined,
      jobCatalogCostCenterName: option?.name,
      jobCatalogNodeUuid: option?.jobCatalogNodeUuid ?? undefined,
      name: option?.name,
    })
  }

  //------------ Update Workplace ------------

  const updateWorkplace = (assignmentId: string, option: CostCenterOption | null) => {
    // If selecting a new cost center, remove it from any other assignments
    if (option) {
      const otherAssignments = assignments.filter((a) => a.id !== assignmentId)
      otherAssignments.forEach((otherAssignment) => {
        if (otherAssignment.workplaceCostCenterId === Number(option.id)) {
          // Remove the workplace from the other assignment
          updateStructuredField(otherAssignment.id, {
            workplaceCostCenterId: undefined,
            workplaceCostCenterName: undefined,
            workplaceId: undefined,
          })
        }
      })
    }

    updateStructuredField(assignmentId, {
      workplaceCostCenterId: option ? Number(option.id) : undefined,
      workplaceCostCenterName: option?.name,
      workplaceId: option?.workplaceId ?? undefined,
      name: option?.name,
    })
  }

  //------------ Update Structured Percentage ------------

  const updateStructuredPercentage = (assignmentId: string, percentage: number) => {
    updateStructuredField(assignmentId, { percentage })
  }

  return {
    addNewStructuredAssignment,
    deleteStructuredAssignment,
    updateJobStructure,
    updateWorkplace,
    updateStructuredPercentage,
  }
}
